import { Text } from "@hackthenorth/north";
import { Button, Table } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
  errorToast,
  PageBar,
  PageWrapper,
  Spinner,
  successToast,
  TooltipWrapper,
  Icon,
} from "src/shared/components";
import Header from "src/shared/components/Header";
import { EVENT_ID } from "src/shared/constants/event";
import ScheduleToggle from "src/views/schedule/components/ScheduleToggle";
import { SCHEDULE_VIEW_TYPE } from "src/views/schedule/constants";
import { useDeleteShiftSignupSelfMutation } from "src/views/volunteer/shift-schedule/graphql/deleteShiftSignupSelf.generated";

import { useMyMentorShiftsQuery } from "../../home/graphql/myMentorShifts.generated";
import MentorRegisterModal from "../../shared/components/MentorRegistrationModal";
import MentorShiftModal from "../../shared/components/MentorShiftModal";
import { useMentorShiftsQuery } from "../../shifts/graphql/mentorShifts.generated";
import {
  DISABLE_REASONS,
  generateDisableReasonToolTip,
  IS_REGISTRATION_OPEN,
} from "../constants";
import { useMentorSignupForShiftMutation } from "../graphql/mentorSignupforShift.generated";
import { TIsDisabled, TIsRegistered, TMentorShiftSchedule } from "../types";
import { parseAdminShifts, handleDownloadCSV } from "../utils";

import MentorShiftCalendarView from "./MentorShiftCalendarView";

export const MentorShiftSchedule = () => {
  const [selectedShift, setSelectedShift] = React.useState<number | null>(null);
  const [viewModal, setViewModal] = React.useState<boolean>(false);

  const [currentView, setCurrentView] = useState<SCHEDULE_VIEW_TYPE>(
    SCHEDULE_VIEW_TYPE.CALENDAR_VIEW
  );

  const [selectedRegister, setSelectedRegister] = React.useState<number | null>(
    null
  );
  const [viewRegisterModal, setViewRegisterModal] = useState<boolean>(false);
  // const [shiftSpotsLeft, setShiftSpotsLeft] = React.useState<number>(0);
  const [actionType, setActionType] = useState<"register" | "unregister">(
    "register"
  );
  const [index, setIndex] = useState<number>(0);

  const [page, setPage] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const [isRegistered, setIsRegistered] = useState<TIsRegistered[]>([]);
  const [isDisableRegistered, setIsDisableRegistered] = useState<TIsDisabled[]>(
    []
  );

  const { data: getMentorShiftsData, loading: fetchingShifts } =
    useMentorShiftsQuery({
      variables: {
        where: {},
      },
      pollInterval: 10000,
    });

  const {
    data: getMyMentorShiftSignups,
    loading: fetchingMyMentorShiftSignups,
    refetch: refetchMyMentorShiftSignups,
  } = useMyMentorShiftsQuery({ variables: { event_id: EVENT_ID } });

  const [signupForShift, signupForShiftResult] =
    useMentorSignupForShiftMutation();
  const [deleteShiftSignup, deleteShiftSignupResult] =
    useDeleteShiftSignupSelfMutation();

  const [adminShifts, setAdminShifts] = useState<TMentorShiftSchedule[]>([]);

  const handleShiftClick = (shift: any) => {
    setSelectedShift(shift.shift_id);
    setViewModal(true);
  };

  const toggleView = () => {
    setCurrentView((currentView) => {
      if (currentView === SCHEDULE_VIEW_TYPE.LIST_VIEW)
        return SCHEDULE_VIEW_TYPE.CALENDAR_VIEW;

      return SCHEDULE_VIEW_TYPE.LIST_VIEW;
    });
  };

  useEffect(() => {
    if (viewModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [viewModal]);

  // REFETCH MY MENTOR SHIFTS AFTER DELETING A SHIFT
  useEffect(() => {
    if (deleteShiftSignupResult.data) {
      refetchMyMentorShiftSignups();
    }
  }, [deleteShiftSignupResult.data, refetchMyMentorShiftSignups]);

  // REFETCH MY MENTOR SHIFTS AFTER SIGNING UP FOR A SHIFT
  useEffect(() => {
    if (signupForShiftResult.data) {
      refetchMyMentorShiftSignups();
    }
  }, [signupForShiftResult.data, refetchMyMentorShiftSignups]);

  useEffect(() => {
    if (getMentorShiftsData && !fetchingShifts) {
      const newShifts = parseAdminShifts(getMentorShiftsData);

      // Sort the shifts by date and then by time_start
      const sortedShifts = newShifts.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA.getTime() - dateB.getTime();
        }
        return a.time_start.getTime() - b.time_start.getTime();
      });

      setAdminShifts(sortedShifts);
      setIsRegistered(
        Array(sortedShifts.length).fill({
          registered: false,
          shouldShow: IS_REGISTRATION_OPEN,
          shift_id: -1,
        })
      );
    }
  }, [getMentorShiftsData, fetchingShifts]);

  useEffect(() => {
    if (
      getMentorShiftsData &&
      !fetchingShifts &&
      getMyMentorShiftSignups &&
      !fetchingMyMentorShiftSignups
    ) {
      const newShifts = parseAdminShifts(getMentorShiftsData);
      const registeredShiftIds =
        getMyMentorShiftSignups.myMentorShifts.map((signup) => signup.id) || [];
      const tempIsRegistered = newShifts.map((shift) => {
        return {
          registered: registeredShiftIds.includes(shift.shift_id),
          shouldShow: IS_REGISTRATION_OPEN,
          shift_id: shift.shift_id,
        };
      });

      setAdminShifts(newShifts);
      setIsRegistered(tempIsRegistered);
    }
  }, [
    getMentorShiftsData,
    fetchingShifts,
    getMyMentorShiftSignups,
    fetchingMyMentorShiftSignups,
  ]);

  useEffect(() => {
    const registeredShiftIds =
      getMyMentorShiftSignups?.myMentorShifts.map((signup) => signup.id) || [];

    const initialIsRegistered = adminShifts.map((shift) => {
      return {
        registered: registeredShiftIds.includes(shift.shift_id),
        shouldShow: IS_REGISTRATION_OPEN,
        shift_id: shift.shift_id,
      };
    });

    setIsRegistered(initialIsRegistered);
  }, [getMyMentorShiftSignups, adminShifts]);

  useEffect(() => {
    const registeredShiftIds = isRegistered
      .filter((item) => item.registered)
      .map((item) => item.shift_id);

    const registeredShifts = adminShifts.filter((shift) =>
      registeredShiftIds.includes(shift.shift_id)
    );

    const initialDisabledNoBothPrimaryRoles = adminShifts.map((shift) => {
      let reason = "";

      //no spots left in the shift
      const hasNoSpotsLeft = shift.spots_left === 0;
      if (hasNoSpotsLeft && reason === "")
        reason = generateDisableReasonToolTip(DISABLE_REASONS.NO_SPOTS_LEFT);

      //time conflict with shift
      const hasTimeConflict = registeredShifts.filter(
        (registeredShift) =>
          registeredShift.time_start < shift.time_end &&
          registeredShift.time_end > shift.time_start &&
          registeredShift.shift_id !== shift.shift_id
      );

      if (hasTimeConflict.length !== 0 && reason === "")
        reason = generateDisableReasonToolTip(
          DISABLE_REASONS.TIME_CONFLICT,
          `${hasTimeConflict[0].shift} at ${
            hasTimeConflict[0].date
          } ${hasTimeConflict[0].time_start.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "America/New_York",
          })} - ${hasTimeConflict[0].time_end.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "America/New_York",
          })}`
        );

      const disabledRegistered = hasNoSpotsLeft || hasTimeConflict.length !== 0;

      return { disabled: disabledRegistered, reason: reason };
    });
    setIsDisableRegistered(initialDisabledNoBothPrimaryRoles);
  }, [adminShifts, getMyMentorShiftSignups, isRegistered]);

  const handleRegister = async (
    shiftId: number,
    index: number,
    spots_left: number
  ) => {
    // setShiftSpotsLeft(spots_left);
    setActionType("register");
    setSelectedRegister(shiftId);
    setIndex(index);
    setViewRegisterModal(true);
  };

  const executeRegister = async (shiftId: number, index: number) => {
    try {
      await signupForShift({ variables: { shift_id: shiftId } });

      successToast("Success! You’ve registered for a shift!");

      setIsRegistered((prev) => [
        ...prev.slice(0, index),
        {
          ...prev[index],
          registered: true,
        },
        ...prev.slice(index + 1),
      ]);
    } catch (error) {
      errorToast("Uh oh! Something went wrong");
      console.error("Error signing up for shift:", error);
    }
  };

  const handleUnregister = async (
    shiftId: number,
    index: number,
    spots_left: number
  ) => {
    // setShiftSpotsLeft(spots_left);
    setActionType("unregister");
    setSelectedRegister(shiftId);
    setIndex(index);
    setViewRegisterModal(true);
  };

  const executeUnregister = async (shiftId: number, index: number) => {
    try {
      await deleteShiftSignup({ variables: { shift_id: shiftId } });
      successToast("Success! You’ve unregistered for a shift!");

      setIsRegistered((prev) => [
        ...prev.slice(0, index),
        {
          ...prev[index],
          registered: false,
        },
        ...prev.slice(index + 1),
      ]);
    } catch (error) {
      errorToast("Uh oh! Something went wrong");
      console.error("Error unregistering from shift:", error);
    }
  };

  const handleModalAction = async (shiftId: number, selectedIndex: number) => {
    if (actionType === "register") {
      executeRegister(shiftId, selectedIndex);
    } else if (actionType === "unregister") {
      executeUnregister(shiftId, selectedIndex);
    }
  };

  const tableData = adminShifts.map(
    (shift: TMentorShiftSchedule, i: number) => {
      return {
        shift: (
          <div
            onClick={() => handleShiftClick(shift)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleShiftClick(shift);
              }
            }}
            role="button"
            tabIndex={0}
            style={{
              cursor: "pointer",
            }}
          >
            {shift?.shift}
          </div>
        ),
        date: shift?.date,
        time_start: (
          <div style={{ textAlign: "right" }}>
            {shift?.time_start
              .toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "numeric",
                hour12: true,
              })
              .replace(":00", "")}
          </div>
        ),
        time_end: (
          <div style={{ textAlign: "right" }}>
            {shift?.time_end
              .toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "numeric",
                hour12: true,
              })
              .replace(":00", "")}
          </div>
        ),
        spots_left: (
          <div style={{ textAlign: "center" }}>{shift?.spots_left}</div>
        ),
        view: (
          <>
            {isRegistered[i]?.shouldShow ? (
              isRegistered[i]?.registered ? (
                <UnregisterButton
                  onClick={() =>
                    handleUnregister(shift.shift_id, i, shift.spots_left)
                  }
                >
                  Unregister
                </UnregisterButton>
              ) : isDisableRegistered.length > 0 &&
                isDisableRegistered[i] !== undefined &&
                isDisableRegistered[i].disabled ? (
                isDisableRegistered[i].reason === "" ? (
                  <DisabledRegisterButton>Register</DisabledRegisterButton>
                ) : (
                  <TooltipWrapper
                    color={"textSecondary"}
                    icon={
                      <DisabledRegisterButton>Register</DisabledRegisterButton>
                    }
                    content={
                      <TooltipText>{isDisableRegistered[i].reason}</TooltipText>
                    }
                    direction="top"
                    eventDelay={0}
                    id={shift.shift + "ToolTip"}
                    padding={8}
                  />
                )
              ) : (
                <RegisterButton
                  onClick={() =>
                    handleRegister(shift.shift_id, i, shift.spots_left)
                  }
                >
                  Register
                </RegisterButton>
              )
            ) : null}
          </>
        ),
      };
    }
  );

  const leftAlignment: "left" | "center" | "right" = "left";
  const centerAlignment: "left" | "center" | "right" = "center";
  const rightAlignment: "left" | "center" | "right" = "right";

  const tableColumns = [
    {
      id: "shift",
      name: "SHIFT",
      align: leftAlignment,
    },
    {
      id: "date",
      name: "DATE",
      align: leftAlignment,
    },
    {
      id: "time_start",
      name: "TIME START",
      align: rightAlignment,
    },
    {
      id: "time_end",
      name: "TIME END",
      align: rightAlignment,
    },
    {
      id: "spots_left",
      name: "SPOTS LEFT",
      align: centerAlignment,
    },
    {
      id: "view",
      name: "View",
      hideLabel: true,
      align: leftAlignment,
    },
  ];

  return (
    <>
      {selectedShift && (
        <MentorShiftModal
          shiftId={selectedShift}
          open={viewModal}
          setOpen={setViewModal}
        />
      )}
      {selectedRegister && (
        <MentorRegisterModal
          shiftId={selectedRegister}
          open={viewRegisterModal}
          setOpen={setViewRegisterModal}
          spotsLeft={
            adminShifts.find((s) => s.shift_id === selectedRegister)
              ?.spots_left ?? 0
          }
          actionType={actionType}
          onAction={handleModalAction}
          index={index}
        />
      )}
      <Header
        title="Shift Schedule"
        subtitle="Register for your mentor shifts"
        actionButton={
          <Button
            leadingIcon={
              <Icon name="spon-download" size="14px" color="transparent" />
            }
            color="primary"
            onClick={() =>
              handleDownloadCSV(adminShifts, "mentor_shift_schedule")
            }
          >
            Download CSV
          </Button>
        }
      />
      <PageWrapper>
        <HeaderFlex>
          <Text mods="heading h3">Schedule</Text>
          <ScheduleToggle
            currentView={currentView}
            onToggle={() => toggleView()}
          ></ScheduleToggle>
        </HeaderFlex>
        <div className="text-text-tertiary mb-4">
          All times are in Eastern Time (EDT)
        </div>
        <div style={{ width: "100%", paddingTop: "32px" }}>
          {fetchingShifts ? (
            <Spinner text="Loading shifts..." />
          ) : currentView === SCHEDULE_VIEW_TYPE.CALENDAR_VIEW ? (
            <>
              <MentorShiftCalendarView
                fetchingShifts={fetchingShifts}
                mentorShifts={adminShifts}
                isRegistered={isRegistered}
                isDisableRegistered={isDisableRegistered}
                handleRegister={handleRegister}
                handleUnregister={handleUnregister}
                setSelectedShift={setSelectedShift}
                setViewModal={setViewModal}
              />
            </>
          ) : (
            <>
              <Table
                columns={tableColumns}
                data={tableData}
                horizontalPadding={16}
                verticalPadding={10}
                showFooter
                page={page}
                itemsPerPage={itemsPerPage}
              />
              <PageBar
                page={page}
                itemsPerPage={itemsPerPage}
                totalItems={tableData.length}
                onPageChange={setPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </>
          )}
        </div>
      </PageWrapper>
    </>
  );
};

const TooltipText = styled.p`
  font-size: 12px;
  color: white;
  font-weight: 700;
`;

export const RegisterButton = styled.button`
  border-radius: 4px;
  border: 1px solid #2395c6;
  background: #43afde;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
  padding: 4px 10px;

  color: #fff;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
`;

export const DisabledRegisterButton = styled.button`
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #88cdea;
  background: #abdbf0;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);

  color: #fff;
  font-size: 16px;
  font-weight: 700;
  text-align: center;

  &:hover {
    cursor: not-allowed;
  }
`;

export const UnregisterButton = styled.button`
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);

  color: #d95858;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
`;

const HeaderFlex = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  flex-direction: row;
  justify-content: space-between;
`;
